import React, { useState, useRef, useLayoutEffect } from 'react';
import { cn } from '@/lib/utils';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import { ControlRow } from '../ControlRow';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { NumberInput } from '@/components/ui/number-input';
import { Switch } from '@/components/ui/switch';
import { RoleCyclerButton } from './RoleCyclerButton';
import { SurchargeDeltaRow } from '../surcharge/SurchargeDeltaRow';
import { BucketBreakdown } from './BucketBreakdown';
import { MethodNavGroup, type NavMethod } from '../MethodNavGroup';
import { RestGapConnector } from './RestGapConnector';
import type { PayBucket } from '@/utils/rateCalculations';
import { SHORT_TURN_HELP_SHEET_CALLOUT_TOP_SPACING } from './helpSheetOpticalSpacing';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface PremiumBracket {
  hoursLabel: string;
  hours: number;
  tierName: string;
  multiplier: number;
  elevated: boolean;
  inViolationWindow: boolean;
  basePay: number;
  premiumAmount: number;
  premiumPerHour: number;
  formula: string;
}

export interface HourlyPremiumBreakdownProps {
  shiftHours: number;
  violationHours: number;
  surchargeAppliesToShortedHoursOnly: boolean;
  premiumType: 'percentage' | 'fixed';
  premiumValue: number;
  premiumBasis: 'base_rate' | 'ot_inclusive';
  brackets: PremiumBracket[];
  basePay: number;
  surchargedPay: number;
  surchargeAmount: number;
  baseBuckets?: PayBucket[];
  surchargedBuckets?: PayBucket[];
  roleContext?: string;
  onRoleCycle?: () => void;
  roleKey?: string | number;
  onBasisChange?: (basis: 'base_rate' | 'ot_inclusive') => void;
  onTypeChange?: (type: 'percentage' | 'fixed') => void;
  onPremiumValueChange?: (value: number | null) => void;
  actualRestHours?: number;
  navMethods?: NavMethod[];
  activeMethodName?: string;
  onNavMethodChange?: (methodValue: string) => void;
  calloutsAboveControls?: React.ReactNode;
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

export const HourlyPremiumBreakdown: React.FC<HourlyPremiumBreakdownProps> = ({
  shiftHours,
  violationHours,
  surchargeAppliesToShortedHoursOnly,
  premiumType,
  premiumValue,
  premiumBasis,
  brackets,
  basePay,
  surchargedPay,
  surchargeAmount,
  baseBuckets,
  surchargedBuckets,
  roleContext,
  onRoleCycle,
  roleKey,
  onBasisChange,
  onTypeChange,
  onPremiumValueChange,
  actualRestHours,
  navMethods,
  activeMethodName,
  onNavMethodChange,
  calloutsAboveControls,
}) => {
  const [expanded, setExpanded] = useState(false);
  const hasControlCallbacks = !!(onTypeChange || onPremiumValueChange || onBasisChange);

  // Track basis/type changes so dollar values animate on toggle (not just role cycle).
  // Stays undefined until the first change so we don't animate on mount.
  const changeCount = useRef(0);
  const prevBasis = useRef(premiumBasis);
  const prevType = useRef(premiumType);
  if (prevBasis.current !== premiumBasis || prevType.current !== premiumType) {
    changeCount.current++;
    prevBasis.current = premiumBasis;
    prevType.current = premiumType;
  }
  const valueAnimKey = changeCount.current > 0
    ? `${roleKey ?? 'v'}-${changeCount.current}`
    : roleKey;

  // Animate content height changes when basis/type switches while expanded
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    if (!expanded || !contentRef.current) { setContentHeight(undefined); return; }
    const el = contentRef.current;
    el.style.height = 'auto';
    const natural = el.scrollHeight;
    if (contentHeight != null && contentHeight !== natural) {
      el.style.height = `${contentHeight}px`;
      void el.offsetHeight;
      el.style.height = `${natural}px`;
    }
    setContentHeight(natural);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [premiumBasis, premiumType, expanded]);

  // Build collapsed formula — uses configured premium values but not role-specific rates.
  // The expanded state shows how it applies to actual roles with dollar amounts.
  const hasFinitePremiumValue = Number.isFinite(premiumValue);
  const fmtPremium = hasFinitePremiumValue
    ? (premiumType === 'fixed'
      ? `$${Number(premiumValue).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}/hr`
      : `${premiumValue}%`)
    : (premiumType === 'fixed' ? '$—/hr' : '—%');

  const isPercentageMode = premiumType === 'percentage';
  const showOtTier = isPercentageMode && premiumBasis === 'ot_inclusive';

  return (
    <div>
      {/* Method nav group */}
      {navMethods && navMethods.length > 1 && activeMethodName && (
        <div className="flex flex-col items-center mb-2 pb-2 border-b border-border/20">
          <MethodNavGroup
            methods={navMethods}
            activeMethodName={activeMethodName}
            onMethodChange={onNavMethodChange}
          />
        </div>
      )}

      <div className="w-full px-0 pt-0 mb-4">
        <div className="w-full flex flex-wrap items-start justify-center gap-x-1 gap-y-2">
          {isPercentageMode ? (
            <>
              <div className="w-full h-6 flex items-center justify-start px-0 text-left font-mono text-[15px] font-semibold tabular-nums">
                <div
                  data-testid="hourly-premium-percentage-formula-row"
                  className="inline-flex items-center gap-3"
                >
                  <span className="text-muted-foreground/75">Premium -</span>
                  <span className="inline-flex items-center rounded-[0.25rem] border border-primary/20 bg-primary/10 px-2 py-1 leading-none text-primary-text">
                    {fmtPremium}
                  </span>
                  <span className="inline-flex items-center gap-3 text-primary-text">
                    <span className="inline-flex items-center leading-none">×</span>
                    <span>Hourly Rate</span>
                  </span>
                  {showOtTier ? (
                    <span className="inline-flex items-center gap-3 text-primary-text">
                      <span className="inline-flex items-center leading-none">×</span>
                      <span>Overtime</span>
                    </span>
                  ) : null}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="w-full h-6 flex items-center justify-start px-0 text-left font-mono text-[15px] font-semibold tabular-nums">
                <div
                  data-testid="hourly-premium-fixed-formula-row"
                  className="inline-flex items-center gap-3"
                >
                  <span className="text-muted-foreground/75">Premium -</span>
                  <span className="inline-flex items-center rounded-[0.25rem] border border-primary/20 bg-primary/10 px-2 py-1 leading-none text-primary-text">
                    {fmtPremium}
                  </span>
                  {premiumBasis === 'ot_inclusive' && (
                    <span className="inline-flex items-center gap-3 text-primary-text">
                      <span className="inline-flex items-center leading-none">×</span>
                      <span>Overtime</span>
                    </span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Rest connector — match short-turn timeline divider behavior */}
      {actualRestHours != null && (
        <RestGapConnector
          hours={actualRestHours}
          triggered
          labelText={surchargeAppliesToShortedHoursOnly ? `${violationHours}hr Shorted` : 'Short Turn'}
          className="mb-2"
        />
      )}

      <button
        type="button"
        onClick={() => setExpanded(prev => !prev)}
        aria-expanded={expanded}
        aria-label={expanded ? 'Hide premium breakdown' : 'Show premium breakdown'}
        className={cn(
          'w-full text-left rounded-[0.25rem] border border-primary/45',
          'bg-primary/[0.04] shadow-[0_0_8px_rgba(232,134,12,0.1)]',
          'min-h-[44px] sm:min-h-0 px-3 py-2 cursor-pointer motion-safe:transition-colors',
          'hover:bg-primary/[0.07]',
          '[border-top-color:rgba(232,134,12,0.35)]',
        )}
      >
        {/* Header row */}
        <div className="relative flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-heading text-[10px] sm:text-[12px] leading-none uppercase tracking-[1px] text-foreground/90">
              Shift
            </span>
            <span
              className={cn(
                'rounded-[0.25rem] px-2 py-1',
                'font-heading text-[10px] sm:text-[12px] uppercase tracking-[1px] leading-none font-bold',
                'bg-primary/20 text-primary-text dark:bg-primary dark:text-primary-foreground',
              )}
            >
              Short Turn
            </span>
          </div>
          <span
            className="pointer-events-none absolute left-1/2 -translate-x-1/2 font-label text-[10px] sm:text-[12px] leading-none whitespace-nowrap"
            style={{ color: 'color-mix(in oklab, var(--color-muted-foreground) 40%, transparent)' }}
          >
            {expanded ? 'Collapse' : 'Expand'}
          </span>
          <span className="inline-flex items-center rounded-[0.25rem] border border-primary/20 bg-primary/10 px-2 py-1 font-mono text-[10px] sm:text-[12px] font-semibold leading-none text-primary-text whitespace-nowrap">
            +{fmtPremium}
          </span>
        </div>

        {/* Expandable content */}
        <AnimatedCollapse open={expanded}>
          <div
            ref={contentRef}
            className="mt-2 overflow-hidden"
            style={{ transition: expanded ? 'height 250ms cubic-bezier(0.4, 0, 0.2, 1)' : undefined }}
          >
            <div className="mb-2 flex items-center gap-2">
              <span className="font-label text-[10px] sm:text-[12px] uppercase tracking-[0.5px] text-muted-foreground/45">
                Shift Length
              </span>
              <span className="font-mono text-[10px] sm:text-[12px] leading-none text-foreground/75">
                {shiftHours}h
              </span>
            </div>

            {/* Role cycler */}
            {roleContext && (
              <RoleCyclerButton
                roleContext={roleContext}
                onRoleCycle={onRoleCycle}
                roleKey={roleKey}
              />
            )}

            {/* Normal Pay */}
            {baseBuckets && baseBuckets.length > 0 && (
              <>
                <span className="font-label text-[10px] sm:text-[12px] uppercase tracking-[0.5px] text-muted-foreground/45 block mb-1">
                  Normal Pay
                </span>
                <BucketBreakdown
                  buckets={baseBuckets}
                  totalLabel="Base Pay"
                  totalAmount={basePay}
                  variant="normal"
                  roleKey={roleKey}
                />
              </>
            )}

            {/* With Short Turn Surcharge */}
            {surchargedBuckets && surchargedBuckets.length > 0 && (
              <>
                <span className="font-label text-[10px] sm:text-[12px] uppercase tracking-[0.5px] text-primary-text block mb-1">
                  With Short Turn Surcharge
                </span>
                <BucketBreakdown
                  buckets={surchargedBuckets}
                  totalLabel="Adjusted Pay"
                  totalAmount={surchargedPay}
                  variant="surcharged"
                  roleKey={valueAnimKey}
                />
              </>
            )}

            {/* Surcharge delta */}
            <SurchargeDeltaRow
              surchargedPay={surchargedPay}
              basePay={basePay}
              surchargeAmount={surchargeAmount}
            />
          </div>
        </AnimatedCollapse>
      </button>

      {/* Adequate-rest divider below the shift card */}
      {actualRestHours != null && (
        <RestGapConnector
          hours={actualRestHours}
          triggered={false}
          subLabel="No Surcharge"
          className="pt-2 mb-2"
        />
      )}

      {calloutsAboveControls && (
        <div className={`${SHORT_TURN_HELP_SHEET_CALLOUT_TOP_SPACING.hourlyPremiumGroup} space-y-2`}>
          {calloutsAboveControls}
        </div>
      )}

      {hasControlCallbacks && (
        <div className={cn(
          calloutsAboveControls ? 'mt-3' : 'mt-2',
          'rounded-[0.25rem] border border-border/30 bg-surface-2/30 divide-y divide-border/20',
        )}>
          {onTypeChange && (
            <ControlRow layout="responsive" label="Premium Type" description="Choose a flat dollar amount per hour or a percentage of your hourly rate.">
              <SegmentedControl
                ariaLabel="Premium Type"
                options={[
                  { value: 'fixed', label: 'Dollar' },
                  { value: 'percentage', label: 'Percentage' },
                ]}
                value={premiumType}
                onChange={(v) => onTypeChange(v as 'percentage' | 'fixed')}
                className="w-full sm:w-auto sm:min-w-[220px] self-start sm:self-auto"
                size="sm"
              />
            </ControlRow>
          )}

          {onPremiumValueChange && (
            <ControlRow
              layout="responsive"
              label="Hourly Premium"
              description={premiumType === 'fixed'
                ? 'Dollar amount added per hour worked.'
                : 'Percentage of your hourly rate added per hour worked.'}
            >
              <NumberInput
                aria-label="Hourly premium"
                className="self-start sm:self-auto"
                size="xs"
                touchTarget
                value={Number.isFinite(premiumValue) ? premiumValue : ''}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '') { onPremiumValueChange(null); return; }
                  const parsed = parseFloat(v);
                  onPremiumValueChange(Number.isFinite(parsed) ? Math.max(0, parsed) : null);
                }}
                unit={premiumType === 'fixed' ? '$' : '%'}
                unitPosition={premiumType === 'fixed' ? 'left' : 'right'}
                step={premiumType === 'fixed' ? 1 : 5}
                minValue={0}
              />
            </ControlRow>
          )}

          {onBasisChange && (
            <ControlRow layout="responsive" label="Scale with Overtime" description="Scale the premium by the active overtime multiplier.">
              <Switch
                aria-label="Scale with overtime"
                className="self-start sm:self-auto"
                checked={premiumBasis === 'ot_inclusive'}
                onCheckedChange={(checked) => {
                  onBasisChange(checked ? 'ot_inclusive' : 'base_rate');
                }}
              />
            </ControlRow>
          )}
        </div>
      )}
    </div>
  );
};
